/* eslint-disable no-nested-ternary */
/* eslint-disable default-case */
import React, { useState, useEffect, useRef } from 'react';

import { logger } from '../utils/logger';

import type {
  ImageData,
  ResizeHandle,
  InteractionState,
} from '../types/pdf.types';

interface ImageOverlayProps {
  image: ImageData;
  onDelete: () => void;
  onPositionChange: (x: number, y: number) => void;
  onSizeChange: (width: number, height: number) => void;
  scale: number; // Current zoom scale
  interactionState: InteractionState;
  isZooming?: boolean;
}

const ImageOverlay: React.FC<ImageOverlayProps> = ({
  image,
  onDelete,
  onPositionChange,
  onSizeChange,
  scale,
  interactionState,
  isZooming = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle | null>(null);
  const [initialSize, setInitialSize] = useState({ width: 0, height: 0 });
  const [initialMousePos, setInitialMousePos] = useState({ x: 0, y: 0 });
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const imageRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const clickedResizeHandle = target.getAttribute('data-resize-handle');

    logger.debug('[ImageOverlay] Mouse down:', {
      imageId: image.id,
      targetTag: target.tagName,
      targetClass: target.className,
      resizeHandle: clickedResizeHandle,
      isResizing,
      clientX: e.clientX,
      clientY: e.clientY,
    });

    if (isResizing) {
      logger.debug('[ImageOverlay] Blocked - already resizing');
      return;
    }

    if (clickedResizeHandle) {
      logger.debug(
        '[ImageOverlay] Blocked - clicked on resize handle:',
        clickedResizeHandle,
      );
      return;
    }

    if (
      interactionState.activeImageId &&
      interactionState.activeImageId !== image.id
    ) {
      logger.debug('[ImageOverlay] Blocked - another image active');
      return;
    }

    logger.debug('[ImageOverlay] Starting drag');
    e.preventDefault();
    e.stopPropagation();

    if (imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  const handleResizeStart = (e: React.MouseEvent, handle: ResizeHandle) => {
    logger.debug('[ImageOverlay] Resize start attempt:', {
      imageId: image.id,
      handle,
      clientX: e.clientX,
      clientY: e.clientY,
      currentSize: image.size,
      currentPosition: image.position,
    });

    if (
      interactionState.activeImageId &&
      interactionState.activeImageId !== image.id
    ) {
      logger.debug('[ImageOverlay] Resize blocked - another image active');
      return;
    }

    e.stopPropagation();
    e.preventDefault();

    logger.debug('[ImageOverlay] Resize starting on handle:', handle);
    setIsResizing(true);
    setResizeHandle(handle);
    setInitialSize({ width: image.size.width, height: image.size.height });
    setInitialPosition({ x: image.position.x, y: image.position.y });
    setInitialMousePos({ x: e.clientX, y: e.clientY });
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();

      if (imageRef.current && imageRef.current.parentElement) {
        const parentRect =
          imageRef.current.parentElement.getBoundingClientRect();
        const newX = e.clientX - parentRect.left - dragOffset.x;
        const newY = e.clientY - parentRect.top - dragOffset.y;

        onPositionChange(newX / scale, newY / scale);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, onPositionChange, scale]);

  useEffect(() => {
    if (!isResizing || !resizeHandle) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();

      const deltaX = e.clientX - initialMousePos.x;
      const deltaY = e.clientY - initialMousePos.y;

      const unscaledDeltaX = deltaX / scale;
      const unscaledDeltaY = deltaY / scale;

      const aspectRatio =
        initialSize.height > 0 ? initialSize.width / initialSize.height : 1;
      let newWidth = initialSize.width;
      let newHeight = initialSize.height;
      let newX = initialPosition.x;
      let newY = initialPosition.y;

      switch (resizeHandle) {
        case 'se': // Southeast corner - proportional resize (anchor: NW)
          newWidth = Math.max(50, initialSize.width + unscaledDeltaX);
          newHeight = newWidth / aspectRatio;
          break;
        case 'nw': // Northwest corner - proportional resize (anchor: SE)
          newWidth = Math.max(50, initialSize.width - unscaledDeltaX);
          newHeight = newWidth / aspectRatio;
          newX = initialPosition.x + (initialSize.width - newWidth);
          newY = initialPosition.y + (initialSize.height - newHeight);
          break;
        case 'ne': // Northeast corner - proportional resize (anchor: SW)
          newWidth = Math.max(50, initialSize.width + unscaledDeltaX);
          newHeight = newWidth / aspectRatio;
          newY = initialPosition.y + (initialSize.height - newHeight);
          break;
        case 'sw': // Southwest corner - proportional resize (anchor: NE)
          newWidth = Math.max(50, initialSize.width - unscaledDeltaX);
          newHeight = newWidth / aspectRatio;
          newX = initialPosition.x + (initialSize.width - newWidth);
          break;
        case 'e': // East edge - horizontal only (anchor: W)
          newWidth = Math.max(50, initialSize.width + unscaledDeltaX);
          newHeight = initialSize.height;
          break;
        case 'w': // West edge - horizontal only (anchor: E)
          newWidth = Math.max(50, initialSize.width - unscaledDeltaX);
          newHeight = initialSize.height;
          newX = initialPosition.x + (initialSize.width - newWidth);
          break;
        case 'n': // North edge - vertical only (anchor: S)
          newWidth = initialSize.width;
          newHeight = Math.max(50, initialSize.height - unscaledDeltaY);
          newY = initialPosition.y + (initialSize.height - newHeight);
          break;
        case 's': // South edge - vertical only (anchor: N)
          newWidth = initialSize.width;
          newHeight = Math.max(50, initialSize.height + unscaledDeltaY);
          break;
      }

      newX = Math.max(0, newX);
      newY = Math.max(0, newY);

      if (newX !== initialPosition.x || newY !== initialPosition.y) {
        onPositionChange(newX, newY);
      }
      onSizeChange(newWidth, newHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeHandle(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    isResizing,
    resizeHandle,
    initialMousePos,
    initialSize,
    initialPosition,
    onSizeChange,
    onPositionChange,
    scale,
  ]);

  const overlayStyle: React.CSSProperties = {
    position: 'absolute',
    left: `${image.position.x * scale}px`,
    top: `${image.position.y * scale}px`,
    zIndex: 20,
    cursor: isDragging ? 'grabbing' : isResizing ? 'auto' : 'grab',
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    opacity: isDragging || isResizing ? 0.8 : isZooming ? 0 : 1,
    transition:
      isDragging || isResizing
        ? 'opacity 0.2s'
        : isZooming
        ? 'none'
        : 'opacity 0.15s ease-out',
    pointerEvents: isZooming ? 'none' : 'auto',
    padding: '10px',
    margin: '-10px', // Offset padding to maintain position
  };

  const deleteButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: '2px', // Adjusted for padding
    right: '2px', // Adjusted for padding
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#ff4d4f',
    color: 'white',
    border: '2px solid white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
    zIndex: 31,
  };

  const getResizeHandleStyle = (handle: ResizeHandle): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      position: 'absolute',
      backgroundColor: 'white',
      border: '2px solid #1890ff',
      zIndex: 30,
      display: isHovered ? 'block' : 'none',
      pointerEvents: 'auto', // Ensure handles capture mouse events
    };

    const cornerStyle: React.CSSProperties = {
      ...baseStyle,
      width: '12px',
      height: '12px',
      borderRadius: '50%',
    };

    const edgeStyle: React.CSSProperties = {
      ...baseStyle,
      backgroundColor: 'white',
    };

    const getCursor = (h: ResizeHandle): string => {
      const cursorMap: Record<ResizeHandle, string> = {
        nw: 'nwse-resize',
        ne: 'nesw-resize',
        se: 'nwse-resize',
        sw: 'nesw-resize',
        n: 'ns-resize',
        s: 'ns-resize',
        e: 'ew-resize',
        w: 'ew-resize',
      };
      return cursorMap[h];
    };

    switch (handle) {
      case 'nw':
        return {
          ...cornerStyle,
          top: '4px',
          left: '4px',
          cursor: getCursor(handle),
        };
      case 'ne':
        return {
          ...cornerStyle,
          top: '4px',
          right: '4px',
          cursor: getCursor(handle),
        };
      case 'se':
        return {
          ...cornerStyle,
          bottom: '4px',
          right: '4px',
          cursor: getCursor(handle),
        };
      case 'sw':
        return {
          ...cornerStyle,
          bottom: '4px',
          left: '4px',
          cursor: getCursor(handle),
        };
      case 'n':
        return {
          ...edgeStyle,
          top: '6px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '20px',
          height: '8px',
          cursor: getCursor(handle),
        };
      case 's':
        return {
          ...edgeStyle,
          bottom: '6px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '20px',
          height: '8px',
          cursor: getCursor(handle),
        };
      case 'e':
        return {
          ...edgeStyle,
          right: '6px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '8px',
          height: '20px',
          cursor: getCursor(handle),
        };
      case 'w':
        return {
          ...edgeStyle,
          left: '6px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '8px',
          height: '20px',
          cursor: getCursor(handle),
        };
    }
  };

  const resizeHandles: ResizeHandle[] = [
    'nw',
    'n',
    'ne',
    'e',
    'se',
    's',
    'sw',
    'w',
  ];

  return (
    <div
      ref={imageRef}
      style={overlayStyle}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => {
        logger.debug('[ImageOverlay] Mouse enter - showing handles');
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        logger.debug('[ImageOverlay] Mouse leave - hiding handles');
        setIsHovered(false);
      }}
    >
      <img
        src={image.url}
        alt="Pasted"
        style={{
          width: `${image.size.width * scale}px`,
          height: `${image.size.height * scale}px`,
          display: 'block',
          pointerEvents: 'none', // Prevent image from interfering with drag
          position: 'relative', // Position relative to padded container
        }}
      />
      <button
        style={deleteButtonStyle}
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        onMouseDown={(e) => e.stopPropagation()} // Prevent drag when clicking delete
        title="Delete image"
      >
        ×
      </button>

      {/* Resize Handles */}
      {resizeHandles.map((handle) => (
        <div
          key={handle}
          data-resize-handle={handle}
          style={getResizeHandleStyle(handle)}
          onMouseDown={(e) => handleResizeStart(e, handle)}
          title={`Resize ${handle}`}
        />
      ))}
    </div>
  );
};

const areEqual = (
  prevProps: ImageOverlayProps,
  nextProps: ImageOverlayProps,
) => {
  return (
    prevProps.image.id === nextProps.image.id &&
    prevProps.image.position.x === nextProps.image.position.x &&
    prevProps.image.position.y === nextProps.image.position.y &&
    prevProps.image.size.width === nextProps.image.size.width &&
    prevProps.image.size.height === nextProps.image.size.height &&
    prevProps.scale === nextProps.scale &&
    prevProps.isZooming === nextProps.isZooming
  );
};

export default React.memo(ImageOverlay, areEqual);
